import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Button,
    Radio,
    RadioGroup,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useDocumentStore } from '../../state/useDocumentStore';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import { ManageDialogFrame, ManageDialogPrimaryButton } from '../manage/ManageDialogFrame';
import type { CentralLocation } from '../../state/useWorksharingStore';

/**
 * Collaborate dialog (Collaborate > Manage Collaboration > Collaborate).
 * BIM's "Collaborate the current project" page: choose between syncing
 * within your network or in the cloud, then the model becomes a central
 * worksharing model.
 */
export function CollaborateDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'collaborate');
    const onClose = useAppStore((state) => state.closeCollabDialog);
    const enabled = useWorksharingStore((s) => s.worksharingEnabled);
    const startWorksharing = useWorksharingStore((s) =>s.startWorksharing); const [location, setLocation] = useState<CentralLocation>('cloud');
    const [path, setPath] = useState('');

    const handleStart = () => {
        const trimmed = path.trim();
        if (!trimmed) return;
        const modelName = useDocumentStore.getState().displayName || 'Current Project';
        startWorksharing(location, trimmed, modelName);
        onClose();
    };

    return (
        <ManageDialogFrame
            title={t('collab.collaborate')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <HStack spacing={2}>
                    <ManageDialogPrimaryButton
                        label={enabled ? 'Worksharing Already Enabled' : 'OK'}
                        onClick={handleStart}
                        isDisabled={enabled || !path.trim()}
                    />
                    <Button size="sm" variant="ghost" onClick={onClose}>{t('collab.cancel')}</Button>
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">
                <Text fontSize="sm">{t('collab.collaborateTheCurrentProjectMakeThisProjectAvailableToTheTeamAsACe')}</Text>
                {enabled && (
                    <Box
                        p={2}
                        borderRadius="var(--cad-radius-sm)"
                        borderWidth="1px"
                        borderColor="var(--color-accent)"
                        bg="var(--color-bg-tertiary)"
                    >
                        <Text fontSize="xs">
                            Worksharing is already enabled for this project
                            ({useWorksharingStore.getState().centralLocation === 'cloud' ? 'in the cloud' : 'on your network'}).
                        </Text>
                    </Box>
                )}
                <RadioGroup value={location} onChange={(v) => setLocation(v as CentralLocation)}>
                    <VStack align="stretch" spacing={2}>
                        <Box>
                            <Radio value="cloud" size="sm" isDisabled={enabled}>
                                <Text fontSize="sm" fontWeight="medium">{t('collab.inTheCloud')}</Text>
                            </Radio>
                            <Text fontSize="xs" color="var(--color-text-secondary)" ml={6}>{t('collab.hostTheCentralModelInTheCloud')}</Text>
                        </Box>
                        <Input
                            size="sm"
                            placeholder={t('collab.projectHubACCFolderEGACMEHub')}
                            value={location === 'cloud' ? path : ''}
                            onChange={(e) => { setLocation('cloud'); setPath(e.target.value); }}
                            isDisabled={enabled || location !== 'cloud'}
                            bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)"
                            ml={6}
                            maxW="420px"
                        />
                        <Box>
                            <Radio value="network" size="sm" isDisabled={enabled}>
                                <Text fontSize="sm" fontWeight="medium">{t('collab.withinYourNetwork')}</Text>
                            </Radio>
                            <Text fontSize="xs" color="var(--color-text-secondary)" ml={6}>{t('collab.saveTheCentralModelToANetworkPathYourTeamCanAccess')}</Text>
                        </Box>
                        <Input
                            size="sm"
                            placeholder={t('collab.serverProjectsBuildingACentralModel')}
                            value={location === 'network' ? path : ''}
                            onChange={(e) => { setLocation('network'); setPath(e.target.value); }}
                            isDisabled={enabled || location !== 'network'}
                            bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)"
                            ml={6}
                            maxW="420px"
                        />
                    </VStack>
                </RadioGroup>
            </VStack>
        </ManageDialogFrame>
    );
}
